import React from 'react';
import { Box, Typography } from '@mui/material';

export interface Segment {
  key: string;
  label: string;
  count: number;
  severity?: string | null;
}

interface SegmentControlProps {
  segments: Segment[];
  active: string;
  onChange: (key: string) => void;
}

const severityColors: Record<string, string> = {
  error: '#DC2626',
  warning: '#D97706',
  success: '#16A34A',
};

export const SegmentControl: React.FC<SegmentControlProps> = ({ segments, active, onChange }) => {
  return (
    <Box
      sx={{
        display: 'inline-flex',
        gap: 0.5,
        p: 0.5,
        bgcolor: '#fff',
        borderRadius: '10px',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      {segments.map(seg => {
        const isActive = seg.key === active;
        return (
          <Box
            key={seg.key}
            onClick={() => onChange(seg.key)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              py: 0.875,
              px: 2,
              borderRadius: '8px',
              cursor: 'pointer',
              bgcolor: isActive ? 'primary.main' : 'transparent',
              transition: 'background-color 0.15s',
              '&:hover': {
                bgcolor: isActive ? 'primary.main' : 'action.hover',
              },
            }}
          >
            {seg.severity && !isActive && (
              <Box
                sx={{
                  width: 5,
                  height: 5,
                  borderRadius: '50%',
                  bgcolor: severityColors[seg.severity] ?? 'grey.400',
                  flexShrink: 0,
                }}
              />
            )}
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.8125rem',
                fontWeight: isActive ? 600 : 500,
                color: isActive ? '#fff' : 'text.primary',
                whiteSpace: 'nowrap',
              }}
            >
              {seg.label}
            </Typography>
            <Box
              sx={{
                minWidth: 20,
                px: 0.75,
                py: 0.125,
                borderRadius: '10px',
                bgcolor: isActive ? 'rgba(255,255,255,0.2)' : 'grey.100',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.6875rem',
                  fontWeight: 600,
                  color: isActive ? '#fff' : 'text.secondary',
                  lineHeight: 1.4,
                }}
              >
                {seg.count}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};
